import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCZK, formatDate } from "@/lib/format";
import { canDeductAdvances, TAX_DOC } from "@/lib/documents";
import { AmountDueField } from "@/components/shared/DocKind";
import { CircleCheck, Info } from "lucide-react";

// Vyúčtování záloh (171). Konečná faktura odečítá zálohové faktury téhož odběratele:
// do Fakturováno se pak počítá jen konečná faktura a k úhradě je jen doplatek.
// 291: stejně u přijaté konečné faktury a záloh dodavatele (náklad je celá konečná faktura).
// Stav výběru žije v `doc.advance_ids` (undefined = nesahat, [] = nic neodečítat).

const gross = (d) => Number(d?.total_with_vat) || Number(d?.total_without_vat) || 0;
const normNum = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();

const STATUS_PILL = {
  "Zaplaceno": "bg-success/15 text-success",
  "Částečně zaplaceno": "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  "Nezaplaceno": "bg-secondary text-muted-foreground",
};

// Záloha už vyúčtovaná jinou fakturou — jen informace v dialogu zálohy.
function SettledNote({ doc, className = "" }) {
  if (doc?.invoice_type !== "zaloha" || !doc?.settled_by_id) return null;
  return (
    <div className={`flex items-start gap-2 rounded-[12px] bg-secondary/60 border border-border p-3 text-sm ${className}`} data-testid="advance-settled-note">
      <CircleCheck size={16} className="text-success shrink-0 mt-0.5" />
      <span>
        <b>Vyúčtováno fakturou č. {doc.settled_by_number || "—"}.</b>{" "}
        <span className="text-muted-foreground">{doc.ui_type === "vydana"
          ? "Do Fakturováno ji už započítává konečná faktura, úhrada zálohy zůstává v inkasu."
          : "Náklad je konečná faktura, zaplacená záloha zůstává ve výdajích."}</span>
      </span>
    </div>
  );
}

export function AdvancesSection({ doc, setDoc, className = "" }) {
  const [cands, setCands] = useState(null);          // null = načítám / nic k načtení
  const initRef = useRef(null);                      // pro který doklad už proběhl předvýběr
  const active = canDeductAdvances(doc);
  const issued = doc?.ui_type === "vydana";
  const party = issued ? "Odběratel" : "Dodavatel";
  const name = (doc?.supplier_name || "").trim();

  useEffect(() => {
    if (!active || name.length < 2) { setCands(null); return; }
    const t = setTimeout(() => {
      const req = issued
        ? api.get("/issued-invoices/advance-candidates", { params: {
          client_name: name, client_ico: doc.supplier_ico || undefined,
          project_id: doc.project_id || undefined, invoice_id: doc.id || undefined } })
        : api.get("/documents/advance-candidates", { params: {
          supplier_name: name, supplier_ico: doc.supplier_ico || undefined,
          project_id: doc.project_id || undefined, document_id: doc.id || undefined } });
      req.then((r) => setCands(r.data || [])).catch(() => setCands([]));
    }, 400);
    return () => clearTimeout(t);
  }, [active, issued, name, doc?.supplier_ico, doc?.project_id, doc?.id]);

  // Předvýběr jen u NOVÉ faktury, jednou: čísla záloh z OCR, jinak u konečné všechny
  // (i když ji člověk na konečnou přepne až ve výběru druhu).
  useEffect(() => {
    if (!cands || !doc || doc.id || doc.advance_ids !== undefined) return;
    const key = `${doc.file_url || ""}|${name}|${doc.invoice_type || ""}`;
    if (initRef.current === key) return;
    initRef.current = key;
    const nums = (doc.advance_numbers || []).map(normNum).filter(Boolean);
    const byNum = nums.length ? cands.filter((c) => nums.includes(normNum(c.invoice_number))) : [];
    if (byNum.length) setDoc((d) => ({ ...d, invoice_type: "konecna", advance_ids: byNum.map((c) => c.id) }));
    else if (doc.invoice_type === "konecna" && cands.length) setDoc((d) => ({ ...d, advance_ids: cands.map((c) => c.id) }));
  }, [cands, doc?.invoice_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dlaždice „K úhradě“ v okně ukazuje doplatek hned podle výběru (uložené součty spočítá server).
  const pickedKey = (doc?.advance_ids || []).join(",");
  useEffect(() => {
    if (!doc) return;
    const ded = active && cands ? cands.filter((c) => (doc.advance_ids || []).includes(c.id)).reduce((s, c) => s + gross(c), 0)
      : active ? null : 0;
    if (ded != null && Math.abs((Number(doc.advances_deducted_gross) || 0) - ded) > 0.005) {
      setDoc((d) => ({ ...d, advances_deducted_gross: ded }));
    }
  }, [cands, active, pickedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!doc) return null;
  if (!active) return <SettledNote doc={doc} className={className} />;
  const list = cands || [];
  const picked = new Set(doc.advance_ids || []);
  const isFinal = doc.invoice_type === "konecna" || picked.size > 0;
  // 291: přijatá konečná bez záloh v systému — „K úhradě“ z faktury ručně (u vydané nikdy).
  const manualDue = !issued && !picked.size && (!list.length || (doc.amount_due != null && doc.amount_due !== ""));
  if (!list.length && !picked.size) {
    return isFinal && cands ? (
      <div className={`space-y-2.5 ${className}`}>
        <p className="text-[13px] text-muted-foreground" data-testid="advances-none">
          {party} nemá {issued ? "žádné nevyúčtované zálohy." : "v systému žádné nevyúčtované zálohy — doplatek opište z faktury."}
        </p>
        {manualDue && <AmountDueField doc={doc} setDoc={setDoc} />}
      </div>
    ) : null;
  }

  // Neurčený typ + existují zálohy: nenápadná nabídka, nic se nepředvybírá samo.
  if (!isFinal) {
    const sum = list.reduce((s, c) => s + gross(c), 0);
    return (
      <div className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-[12px] border border-border bg-secondary/40 p-3 ${className}`} data-testid="advances-offer">
        <Info size={16} className="text-brand shrink-0 hidden sm:block" />
        <span className="text-sm flex-1 min-w-0">
          {party} má {list.length === 1 ? "1 nevyúčtovanou zálohu" : `${list.length} nevyúčtované zálohy`} ({formatCZK(sum)}). Je to konečná faktura?
        </span>
        <button type="button" data-testid="advances-offer-btn"
          onClick={() => setDoc((d) => ({ ...d, invoice_type: "konecna", advance_ids: list.map((c) => c.id) }))}
          className="h-10 px-4 rounded-full border border-border bg-card text-sm font-bold hover:bg-secondary w-full sm:w-auto shrink-0">
          Odečíst zálohy
        </button>
      </div>
    );
  }

  const toggle = (id) => setDoc((d) => {
    const s = new Set(d.advance_ids || []);
    if (s.has(id)) s.delete(id); else s.add(id);
    return { ...d, advance_ids: [...s] };
  });
  const total = gross(doc);
  const deducted = list.filter((c) => picked.has(c.id)).reduce((s, c) => s + gross(c), 0);
  const over = total > 0 && deducted > total + 0.5;
  const pending = doc.id && (doc.advances_pending || 0) > 0 && picked.size === 0;

  return (
    <div className={`rounded-[14px] border border-border overflow-hidden ${className}`} data-testid="advances-section">
      <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 bg-secondary/50 border-b border-border">
        <span className="text-sm font-bold">Odečtené zálohy</span>
        {list.length > 1 && (
          <button type="button" data-testid="advances-all"
            onClick={() => setDoc((d) => ({ ...d, advance_ids: picked.size === list.length ? [] : list.map((c) => c.id) }))}
            className="text-xs font-bold text-brand hover:underline min-h-[32px]">
            {picked.size === list.length ? "Zrušit výběr" : "Vybrat všechny"}
          </button>
        )}
      </div>
      {pending && (
        <p className="px-3 sm:px-4 pt-2.5 text-[13px] font-semibold text-amber-600 dark:text-amber-400" data-testid="advances-pending-hint">
          Zálohy k vyúčtování — zaškrtněte, které tato faktura odečítá, a uložte.
        </p>
      )}
      <div className="divide-y divide-border/60">
        {list.map((c) => (
          <label key={c.id} data-testid={`advance-row-${c.id}`}
            className="flex items-center gap-3 px-3 sm:px-4 py-2.5 min-h-[52px] cursor-pointer hover:bg-secondary/30">
            <Checkbox checked={picked.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`advance-check-${c.id}`} />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold truncate">Záloha č. {c.invoice_number || "—"}</span>
              <span className="block text-xs text-muted-foreground truncate">
                {c.issue_date ? `vystaveno ${formatDate(c.issue_date)}` : "bez data"}
                <span className="sm:hidden"> · {c.payment_status}</span>
              </span>
            </span>
            <span className={`hidden sm:inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_PILL[c.payment_status] || STATUS_PILL.Nezaplaceno}`}>
              {c.payment_status}
            </span>
            <span className="shrink-0 text-sm font-bold tabular-nums whitespace-nowrap text-right sm:w-32">{formatCZK(gross(c))}</span>
          </label>
        ))}
      </div>
      <div className="border-t border-border bg-secondary/30 px-3 sm:px-4 py-3 flex sm:justify-end" data-testid="advances-summary">
        <dl className="w-full sm:w-auto sm:min-w-[260px] grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm tabular-nums">
          <dt className="text-muted-foreground">Celkem faktura</dt><dd className="text-right whitespace-nowrap">{formatCZK(total)}</dd>
          <dt className="text-muted-foreground">Odečtené zálohy</dt><dd className="text-right whitespace-nowrap">−{formatCZK(deducted)}</dd>
          <dt className="font-bold pt-1 border-t border-border">K úhradě</dt>
          <dd className="font-heading font-extrabold text-right whitespace-nowrap pt-1 border-t border-border" data-testid="advances-due">{formatCZK(Math.max(total - deducted, 0))}</dd>
        </dl>
      </div>
      {over && (
        <p className="px-3 sm:px-4 pb-3 -mt-1 text-xs font-semibold text-destructive" data-testid="advances-over">
          Zálohy převyšují částku faktury — zkontrolujte výběr nebo částku.
        </p>
      )}
      {manualDue && <AmountDueField doc={doc} setDoc={setDoc} className="px-3 sm:px-4 py-3 border-t border-border" />}
    </div>
  );
}

// 288: Daňový doklad k přijaté platbě — tytéž peníze jako záloha. Připojí se k záloze,
// ke které patří; tržby (náklady), DPH ani platby se pak nesečtou dvakrát. Bez výběru dohledá
// server sám (číslo zálohy na dokladu, jinak stejná částka). „none“ = samostatná záloha.
// 291: i u přijatých faktur (záloha od dodavatele).
export function TaxDocPicker({ doc, setDoc, className = "" }) {
  const [cands, setCands] = useState(null);
  const issued = doc?.ui_type === "vydana";
  const on = !!doc && doc.invoice_type === TAX_DOC && !doc.is_credit_note;
  const name = (doc?.supplier_name || "").trim();

  useEffect(() => {
    if (!on || name.length < 2) { setCands(null); return; }
    const t = setTimeout(() => {
      const req = issued
        ? api.get("/issued-invoices/advance-candidates", { params: {
          client_name: name, client_ico: doc.supplier_ico || undefined,
          project_id: doc.project_id || undefined, invoice_id: doc.id || undefined, tax_doc: 1 } })
        : api.get("/documents/advance-candidates", { params: {
          supplier_name: name, supplier_ico: doc.supplier_ico || undefined,
          project_id: doc.project_id || undefined, document_id: doc.id || undefined, tax_doc: 1 } });
      req.then((r) => setCands(r.data || [])).catch(() => setCands([]));
    }, 400);
    return () => clearTimeout(t);
  }, [on, issued, name, doc?.supplier_ico, doc?.project_id, doc?.id]);

  // Předvýběr jednou: číslo zálohy z dokladu, jinak jediná záloha se stejnou částkou.
  useEffect(() => {
    if (!cands || !on || doc.for_advance_id !== undefined) return;
    const nums = (doc.advance_numbers || []).map(normNum).filter(Boolean);
    const same = cands.filter((c) => Math.abs(gross(c) - gross(doc)) <= 1);
    const hit = cands.find((c) => nums.includes(normNum(c.invoice_number))) || (same.length === 1 ? same[0] : null);
    if (hit) setDoc((d) => ({ ...d, for_advance_id: hit.id }));
  }, [cands]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!on || !cands) return null;
  if (!cands.length) {
    return (
      <p className={`text-[13px] text-muted-foreground ${className}`} data-testid="taxdoc-none">
        {issued ? "Odběratel" : "Dodavatel"} nemá zálohu bez dokladu k platbě — uloží se jako zaplacená záloha.
      </p>
    );
  }
  const pick = (id) => setDoc((d) => ({ ...d, for_advance_id: id }));
  const rows = [...cands.map((c) => ({ id: c.id, title: `Záloha č. ${c.invoice_number || "—"}`,
    sub: `${c.issue_date ? formatDate(c.issue_date) : "bez data"} · ${c.payment_status}`, amount: formatCZK(gross(c)) })),
    { id: "none", title: "Žádná z nich", sub: "uložit jako samostatnou zaplacenou zálohu", amount: "" }];
  return (
    <div className={`rounded-[14px] border border-border overflow-hidden ${className}`} role="radiogroup" aria-label="Ke které záloze doklad patří" data-testid="taxdoc-picker">
      <div className="px-3 sm:px-4 py-2.5 bg-secondary/50 border-b border-border text-sm font-bold">Ke které záloze patří</div>
      <div className="divide-y divide-border/60">
        {rows.map((r) => {
          const on2 = (doc.for_advance_id ?? null) === r.id;
          return (
            <button key={r.id} type="button" role="radio" aria-checked={on2} onClick={() => pick(r.id)} data-testid={`taxdoc-row-${r.id}`}
              className={`w-full text-left flex items-center gap-3 px-3 sm:px-4 py-2.5 min-h-[52px] ${on2 ? "bg-brand/[0.07]" : "hover:bg-secondary/30"}`}>
              <span className={`h-5 w-5 shrink-0 rounded-full border-2 grid place-items-center ${on2 ? "border-brand" : "border-muted-foreground/50"}`}>
                {on2 && <span className="h-2.5 w-2.5 rounded-full bg-brand" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold truncate">{r.title}</span>
                <span className="block text-xs text-muted-foreground truncate">{r.sub}</span>
              </span>
              {r.amount && <span className="shrink-0 text-sm font-bold tabular-nums whitespace-nowrap">{r.amount}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Uložená záloha s připojeným dokladem k platbě (288).
export function TaxDocNote({ doc, className = "" }) {
  const td = doc?.tax_doc;
  if (!td || doc.invoice_type !== "zaloha") return null;
  return (
    <div className={`flex items-start gap-2 rounded-[12px] bg-secondary/60 border border-border p-3 text-sm ${className}`} data-testid="taxdoc-note">
      <CircleCheck size={16} className="text-success shrink-0 mt-0.5" />
      <span>
        <b>Doklad k platbě č. {td.number || "—"}{td.date ? ` ze dne ${formatDate(td.date)}` : ""}.</b>{" "}
        <span className="text-muted-foreground">{doc.ui_type === "vydana" ? "Patří k této záloze — tržby, DPH i inkaso se počítají jednou."
          : "Patří k této záloze — platba ani DPH se nepočítají dvakrát."}</span>
      </span>
    </div>
  );
}
